import React, { useState, useEffect } from 'react';
import MasterCard from '../components/Dashboard/MasterCard';
import ObjectComparison from '../components/Dashboard/ObjectComparison';
import ExportDashboardButton from '../components/Dashboard/ExportDashboardButton';
import ModernGanttChart from '../components/Dashboard/ModernGanttChart';
import client from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { saveSelectedCity, getSelectedCity } from '../utils/userPreferences';
import { createScheduleUpdateHandler } from '../utils/scheduleUpdateWithCascade';

const Home = () => {
  const [cities, setCities] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [selectedCity, setSelectedCity] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { showError } = useToast();

  useEffect(() => {
    fetchCities();
  }, []);

  useEffect(() => {
    fetchSchedules();
  }, [selectedCity]);

  const fetchCities = async () => {
    try {
      const response = await client.get('/cities');
      const visibleCities = (response.data || []).filter(
        (city) => city.visible_in_schedules !== false
      );
      setCities(visibleCities);

      // Восстанавливаем сохранённый город (только если он ещё виден)
      const savedCity = getSelectedCity();
      if (
        savedCity != null &&
        visibleCities.some((c) => Number(c.id) === Number(savedCity))
      ) {
        setSelectedCity(Number(savedCity));
      } else if (savedCity != null) {
        setSelectedCity(null);
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
      showError('Ошибка при загрузке объектов');
    }
  };

  const loadSchedules = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      const params = selectedCity ? { city_id: selectedCity } : {};
      const response = await client.get('/schedules', { params });
      setSchedules(response.data);
    } catch (error) {
      console.error('Error fetching schedules:', error);
      showError('Ошибка при загрузке графиков');
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const fetchSchedules = () => loadSchedules(true);
  const fetchSchedulesBackground = () => loadSchedules(false);

  // Обработчик обновления дат из диаграммы Ганта (с каскадом для связанных задач)
  const handleScheduleUpdate = createScheduleUpdateHandler({
    fetchSchedules: fetchSchedulesBackground,
    showError,
    cityId: selectedCity,
    backgroundRefetch: true
  });

  const handleCityChange = (value) => {
    const cityId = value ? Number(value) : null;
    const next = Number.isFinite(cityId) && cityId > 0 ? cityId : null;
    setSelectedCity(next);
    if (next) {
      saveSelectedCity(next);
    } else {
      saveSelectedCity(null);
    }
  };

  return (
    <div className="container-fluid">
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <div>
          <h1 style={{ marginBottom: '5px' }}>Панель управления</h1>
          <p style={{ color: 'var(--text-muted)', margin: 0 }}>
            Добро пожаловать, {user?.username}! 
            <span style={{ marginLeft: '10px', fontSize: '13px' }}>
              💡 Нажмите <kbd style={{
                backgroundColor: 'var(--bg-secondary)',
                border: '1px solid var(--border-color)',
                borderRadius: '3px',
                padding: '1px 5px',
                fontSize: '11px',
                fontFamily: 'monospace'
              }}>?</kbd> для справки по горячим клавишам
            </span>
          </p>
        </div>
        {(user?.role === 'admin' || user?.role === 'director') && (
          <ExportDashboardButton cityId={selectedCity} />
        )}
      </div>
      
      <div className="card" style={{ maxWidth: '600px' }}>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            🏗️ Выберите объект строительства
          </label>
          <select 
            className="form-control"
            value={selectedCity || ''}
            onChange={(e) => handleCityChange(e.target.value)}
            style={{ fontSize: '16px', padding: '10px 12px' }}
          >
            <option value="">Все объекты</option>
            {cities.map(city => (
              <option key={city.id} value={city.id}>{city.name}</option>
            ))}
          </select>
        </div>
      </div>

      <MasterCard cityId={selectedCity} />

      {(user?.role === 'admin' || user?.role === 'director') && (
        <ObjectComparison />
      )}

      {(user?.role === 'admin' || user?.role === 'director') && (
        <div className="card-full-width">
          {loading ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '60px 40px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '15px'
            }}>
              <div className="loading-spinner" style={{ width: '40px', height: '40px' }}></div>
              <div style={{ color: 'var(--text-muted)' }}>Загрузка графика...</div>
            </div>
          ) : (
            <ModernGanttChart 
              schedules={schedules} 
              cities={cities} 
              onScheduleUpdate={user?.role === 'director' ? undefined : handleScheduleUpdate}
              defaultDragDisabled={true}
              dependencyCityId={selectedCity || undefined}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default Home;
